<template>
  <v-dialog v-model="dialog" fullscreen hide-overlay>
    <v-toolbar dark color="#2d8659">
      <v-btn icon dark @click="handleClose">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>
        Formular de Decontare Nr. {{ formData ? formData.id : '' }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        v-if="formData && formData.status === 'draft' && canUpdateStatus"
        color="white"
        text
        @click="showEditForm = true"
        class="mr-2"
      >
        <v-icon left small>mdi-pencil</v-icon>
        Editează
      </v-btn>
      <v-btn
        v-if="formData && canUpdateStatus"
        color="white"
        text
        @click="showCopyForm = true"
        class="mr-2"
        title="Formular nou cu aceleași date (de ex. aceleași articole pentru altă persoană)"
      >
        <v-icon left small>mdi-content-copy</v-icon>
        Copiază
      </v-btn>
      <v-btn
        v-if="formData"
        color="white"
        text
        @click="printForm('defecte')"
        class="mr-2"
      >
        <v-icon left small>mdi-file-word</v-icon>
        Defecte
      </v-btn>
      <v-btn
        v-if="formData"
        color="white"
        text
        @click="printForm('fara_defecte')"
        class="mr-2"
      >
        <v-icon left small>mdi-file-word-outline</v-icon>
        Fără defecte
      </v-btn>
      <v-btn
        v-if="formData"
        color="white"
        text
        @click="printProcesVerbal()"
        class="mr-2"
      >
        <v-icon left small>mdi-file-document</v-icon>
        Proces-Verbal
      </v-btn>
      <v-btn
        v-if="formData && formData.status === 'draft' && canUpdateStatus && canConfirm"
        color="white"
        text
        @click="confirmFormAction"
        :loading="confirming"
        class="mr-2"
      >
        <v-icon left small>mdi-check-circle</v-icon>
        Confirmă
      </v-btn>
      <v-btn
        v-if="formData && formData.status === 'draft' && canUpdateStatus"
        color="red lighten-1"
        text
        @click="dialogDelete = true"
        class="mr-2"
      >
        <v-icon left small>mdi-delete</v-icon>
        Șterge
      </v-btn>
      <v-chip
        v-if="formData"
        :color="formData.status === 'confirmed' ? 'success' : 'warning'"
        text-color="white"
        class="mr-2"
      >
        {{ formData.status === 'confirmed' ? 'Confirmat' : 'Ciornă' }}
      </v-chip>
    </v-toolbar>

    <v-card color="white">
      <div class="space"></div>
      <ErrorAlert :message="errorMessage" @clear-error="clearMessage" />
      <SuccessAlert :message="successMessage" @clear-error="clearMessage" />

      <v-card-text v-if="formData">
        <v-container>
          <!-- Form info -->
          <v-row>
            <v-col cols="12" sm="3">
              <v-text-field
                :value="formData.id"
                label="Nr. Formular"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                :value="formatDate(formData.created_at)"
                label="Data creării"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                :value="formData.department"
                label="Departament"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                :value="createdByName"
                label="Creat de"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row v-if="formData.notes">
            <v-col cols="12">
              <v-textarea
                :value="formData.notes"
                label="Note"
                readonly
                rows="2"
                auto-grow
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Items table -->
          <v-row class="mt-4">
            <v-col cols="12">
              <h3 class="custom-heading mb-2">Articole</h3>
              <v-simple-table>
                <thead>
                  <tr style="background-color: #2d8659;">
                    <th class="white--text">Nr</th>
                    <th class="white--text">Categorie</th>
                    <th class="white--text">Codul</th>
                    <th class="white--text">Denumire</th>
                    <th class="white--text">Persoana</th>
                    <th class="white--text">Cantitatea</th>
                    <th class="white--text">Preț unitar</th>
                    <th class="white--text">Anul fabricării</th>
                    <th class="white--text">Data punerii</th>
                    <th class="white--text">Durata funcț. (luni)</th>
                    <th class="white--text">Ani folosirii</th>
                    <th class="white--text">Luni</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(item, idx) in formData.items">
                    <tr :key="'item-' + item.id" :class="{ 'light-green-row': idx % 2 !== 0 }">
                      <td>{{ idx + 1 }}</td>
                      <td>{{ item.categoryEntity ? item.categoryEntity.denumire : '' }}</td>
                      <td>{{ item.codul || '' }}</td>
                      <td>{{ item.denumire }}</td>
                      <td>{{ item.persoana || '' }}</td>
                      <td>{{ item.cantitatea }}</td>
                      <td>{{ Number(item.pret || 0).toFixed(2) }}</td>
                      <td>{{ item.anul_fabricatie || '' }}</td>
                      <td>{{ formatDateShort(item.data_punerii) }}</td>
                      <td>{{ monthDiff(item.data_punerii, formData.created_at) }}</td>
                      <td>{{ item.anul_folosirii }}</td>
                      <td>{{ item.lunii }}</td>
                    </tr>
                    <!-- Defect description -->
                    <tr :key="'defect-' + item.id" v-if="item.defect_description">
                      <td></td>
                      <td colspan="11">
                        <div class="pa-2 defect-row">
                          <v-icon small color="red darken-1" class="mr-1">mdi-alert-circle-outline</v-icon>
                          <span class="grey--text text--darken-2">{{ item.defect_description }}</span>
                        </div>
                      </td>
                    </tr>
                    <!-- Inventory numbers -->
                    <tr :key="'inv-header-' + item.id" v-if="item.inventoryNumbers && item.inventoryNumbers.length > 0">
                      <td></td>
                      <td colspan="11">
                        <div class="pa-2">
                          <strong>Nr. Inventar:</strong>
                          <v-chip
                            v-for="inv in item.inventoryNumbers"
                            :key="inv.id"
                            class="ma-1"
                            :color="getStatusColor(inv.status)"
                            text-color="white"
                            small
                          >
                            {{ inv.nr_inventar }}
                            <span class="ml-1">({{ getStatusLabel(inv.status) }})</span>
                            <template v-if="canUpdateStatus">
                              <v-btn
                                v-if="inv.status === 'pending'"
                                icon
                                x-small
                                class="ml-1"
                                @click="updateStatus(inv.id, 'in_process')"
                                title="Început prelucrare"
                              >
                                <v-icon x-small color="white">mdi-minus</v-icon>
                              </v-btn>
                              <v-btn
                                v-if="inv.status === 'in_process'"
                                icon
                                x-small
                                class="ml-1"
                                @click="updateStatus(inv.id, 'finished')"
                                title="Finalizat"
                              >
                                <v-icon x-small color="white">mdi-plus</v-icon>
                              </v-btn>
                            </template>
                          </v-chip>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-text v-else class="text-center pa-8">
        <v-progress-circular indeterminate color="#2d8659"></v-progress-circular>
      </v-card-text>
    </v-card>

    <!-- Delete confirmation -->
    <v-dialog v-model="dialogDelete" persistent max-width="500">
      <v-card>
        <v-card-title class="headline">Ștergere formular</v-card-title>
        <v-card-text>Sigur doriți să ștergeți formularul nr. {{ formData ? formData.id : '' }}?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="deleteForm">Da</v-btn>
          <v-btn color="red darken-1" text @click="dialogDelete = false">Nu</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit form -->
    <DecontareForm
      v-if="showEditForm"
      :editData="formData"
      @close="showEditForm = false"
      @success="handleEditSuccess"
      @error="handleEditError"
    />

    <!-- Copy into a new form -->
    <DecontareForm
      v-if="showCopyForm"
      :editData="formData"
      copy
      @close="showCopyForm = false"
      @success="handleCopySuccess"
      @error="handleEditError"
    />
  </v-dialog>

</template>

<script>
import * as api from "./api";
import DecontareForm from "./DecontareForm.vue";
import { Document, Packer, Paragraph, TextRun, AlignmentType, TabStopType, LevelFormat, UnderlineType, Table, TableRow, TableCell, WidthType, BorderStyle, VerticalAlign } from "docx";
import { saveAs } from "file-saver";
import { mapGetters } from "vuex";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import SuccessAlert from "@/components/alerts/SuccessAlert.vue";

export default {
  name: "DecontareDetails",
  props: ["formId"],
  components: {
    ErrorAlert,
    SuccessAlert,
    DecontareForm,
  },
  data() {
    return {
      dialog: true,
      formData: null,
      errorMessage: "",
      successMessage: "",
      confirming: false,
      dialogDelete: false,
      showEditForm: false,
      showCopyForm: false,
    };
  },
  methods: {
    async fetchForm(id) {
      try {
        this.formData = await api.getFormById(id);
      } catch (error) {
        this.$emit("error", "Eroare la încărcarea formularului");
      }
    },
    // staff often write the inventory number in "Codul" (items under the threshold have no Nr. Inventar fields)
    inventoryNumbersOf(item) {
      const numbers = (item.inventoryNumbers || []).map(inv => inv.nr_inventar).filter(Boolean);
      if (numbers.length) return numbers;
      return item.codul && item.codul.trim() ? [item.codul.trim()] : [];
    },
    getStatusColor(status) {
      if (status === 'finished') return 'success';
      if (status === 'in_process') return 'orange';
      return 'grey';
    },
    getStatusLabel(status) {
      if (status === 'finished') return 'Finalizat';
      if (status === 'in_process') return 'În proces';
      return 'În așteptare';
    },
    async printForm(mode) {
      try {
        const isDefecte = mode === 'defecte';
        const [experts, settings] = await Promise.all([
          api.getExperts(),
          api.getSettings(),
        ]);
        const year = String(new Date().getFullYear());
        const ordinNr = settings.ordin_nr || '_____';
        const ordinData = settings.ordin_data || '_____';
        const RO = { language: { value: "ro-RO" } };
        const RO_MD = { language: { value: "ro-MD" } };
        const EN = { language: { value: "en-US" } };
        const roRun = (text, opts = {}) => new TextRun({ text, font: "Times New Roman", ...RO, ...opts });
        const boldRoRun = (text, opts = {}) => new TextRun({ text, font: "Times New Roman", bold: true, ...RO, ...opts });
        const tabRun = (opts = {}) => new TextRun({ children: ["\t"], font: "Times New Roman", ...RO, ...opts });
        const expertsList = experts.map(e => `${e.name}, ${e.position || ''}`).join(', ');

        const sections = this.formData.items.map(item => {
          const defectLines = item.defect_description
            ? item.defect_description.split('\n').filter(l => l.trim())
            : [];
          const fabricYear = item.anul_fabricatie ? String(item.anul_fabricatie) : '_____';
          const price = Number(item.pret || 0).toFixed(2).replace('.', ',');

          const concluziiText = isDefecte
            ? `Concluzii:  ${item.denumire || '___'} este defectat, trebuie să fie decontat. `
            : `Concluzii:  ${item.denumire || '___'} este învechit și nu mai corespunde cerințelor actuale ale software-ului utilizat, motiv pentru care se propune casarea acestuia. `;

          // Defect section paragraphs (both modes: heading + numbered items, no "comisia a constatat")
          const defectParagraphs = [
            // Defects heading
            new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 0, line: 240 }, children: [
              new TextRun({ text: "Defectele depistate și caracterul lor", font: "Times New Roman", bold: true, size: 28, ...RO }),
            ]}),
            new Paragraph({ spacing: { after: 0, line: 240 }, indent: { left: 708 }, children: [] }),
            // Defect numbered items
            ...(defectLines[0] ? [new Paragraph({ numbering: { reference: "defect-list", level: 0 }, alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [roRun(defectLines[0])] })] : []),
            ...(defectLines[1] ? [new Paragraph({ numbering: { reference: "defect-list", level: 0 }, alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [roRun(defectLines[1])] })] : []),
            ...(defectLines.length > 2
              ? defectLines.slice(2).map(l => new Paragraph({ numbering: { reference: "defect-list", level: 0 }, alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [roRun(l)] }))
              : [new Paragraph({ numbering: { reference: "defect-list", level: 0 }, alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [] })]),
            new Paragraph({ spacing: { after: 0, line: 240 }, indent: { left: 720 }, children: [] }),
          ];

          return {
            properties: {
              page: {
                size: { width: 11906, height: 16838 },
                margin: { top: 851, right: 850, bottom: 1134, left: 1701 },
              },
            },
            children: [
              // Header
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 0, line: 240 }, children: [
                new TextRun({ text: "Ministerul Educației al Republicii Moldova", font: "Times New Roman", size: 28, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 0, line: 240 }, children: [
                new TextRun({ text: 'Universitatea de Stat \u201CAlecu Russo\u201D din Bălți', font: "Times New Roman", size: 28, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 0, line: 240 }, children: [
                new TextRun({ text: "", font: "Times New Roman", size: 28, ...RO }),
              ]}),
              // Title
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 0, line: 360 }, children: [
                new TextRun({ text: "Act de defectare nr.      ", font: "Times New Roman", bold: true, size: 36, ...RO }),
              ]}),
              // Date line
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 0, line: 240 },
                tabStops: [{ type: TabStopType.RIGHT, position: 9355 }],
                children: [
                  new TextRun({ text: '\u201D  \u201D', font: "Times New Roman", underline: { type: UnderlineType.SINGLE }, ...RO }),
                  roRun(" _________________"),
                  new TextRun({ text: " 202", font: "Times New Roman", underline: { type: UnderlineType.SINGLE }, ...RO }),
                  new TextRun({ text: year.slice(-1), font: "Times New Roman", underline: { type: UnderlineType.SINGLE }, ...EN }),
                  new TextRun({ text: " ", font: "Times New Roman", underline: { type: UnderlineType.SINGLE }, ...RO }),
                  tabRun(),
                  roRun("m. Bălți"),
                ],
              }),
              // Small spacer
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                new TextRun({ text: "", font: "Times New Roman", size: 16, ...RO }),
              ]}),
              // Main body paragraph
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                tabRun(),
                roRun('Noi, subsemnații, experți pentru aprecierea calității utilajului din Universitatea de Stat \u201CAlecu Russo\u201D din Bălți, '),
                roRun(expertsList),
                roRun(', acționând în baza ordinului rectorului '),
                boldRoRun(`nr. ${ordinNr} din ${ordinData}`),
                roRun(', am efectuat cercetarea utilajului '),
                new TextRun({ text: item.denumire || '___', font: "Times New Roman", bold: true, ...RO }),
                roRun(`, cantitatea ${item.cantitatea || 1} buc,. prețul ${price} de lei; anul de fabr. ${fabricYear}, nr.${item.codul || this.inventoryNumbersOf(item).join(', ') || '_____'}`),
              ]}),
              // Defect section (only for "defecte" mode)
              ...defectParagraphs,
              // Concluzii
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                new TextRun({ text: concluziiText, font: "Times New Roman", underline: { type: UnderlineType.SINGLE }, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [] }),
              // Piese prezente
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                roRun("Toate piesele și componentele utilajului cercetat sunt prezente la locul lor."),
              ]}),
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                roRun("Lipsesc din utilaj următoarele piese: _______________________ prețul ___________________ lei."),
              ]}),
              // Concluzie
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 0, line: 240 }, children: [
                new TextRun({ text: "Concluzie", font: "Times New Roman", bold: true, size: 28, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, indent: { firstLine: 720 }, children: [
                roRun("Utilajul cercetat poate fi trecut la pierderi și să fie scos de la evidența (d-nei"),
                new TextRun({ text: ')  ', font: "Times New Roman", bold: true, ...RO }),
                new TextRun({ text: item.persoana || '_________________', font: "Times New Roman", bold: true, ...RO }),
                roRun(' Următoarele piese din acest utilaj, după aprobarea actului de decontare, se transmit în laboratorul persoanei răspunzătoare de bunuri materiale  _____________.'),
              ]}),
              // Piese în stare bună
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 0, line: 240 }, children: [
                new TextRun({ text: "Piese în stare bună:", font: "Times New Roman", bold: true, size: 28, ...RO }),
              ]}),
              ...Array.from({ length: 4 }, (_, i) => new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                roRun(`${i + 1}. ___________________________ Cant. ____ buc. ${i < 2 ? "p" : "P"}rețul pentru o buc. _____. Suma ______`),
              ]})),
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [] }),
              // Metale
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                roRun("Metalele prețioase extrase din utilaj:"),
              ]}),
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, indent: { left: 2880, firstLine: 720 }, children: [
                roRun("    Aur _________________________________ g."),
              ]}),
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, indent: { left: 2880, firstLine: 720 }, children: [
                roRun("Argint _________________________________ g."),
              ]}),
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                roRun("          Metalele prețioase le primește _______________________"),
              ]}),
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [] }),
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 360 }, children: [
                roRun("Metalele feroase _____________________ kg le primește ___________________ "),
              ]}),
              // Experți
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 360 }, children: [
                roRun("Experți:   "),
              ]}),
              ...experts.map(e => new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 360 }, indent: { firstLine: 708 }, children: [
                roRun("   "),
                new TextRun({ text: e.name, font: "Times New Roman", ...RO_MD }),
                roRun("    ____________"),
              ]})),
            ],
          };
        });

        const doc = new Document({
          styles: {
            default: {
              document: {
                run: { font: "Times New Roman", size: 24 },
                paragraph: { spacing: { after: 0, line: 240, lineRule: "auto" } },
              },
            },
          },
          numbering: {
            config: [{
              reference: "defect-list",
              levels: [{
                level: 0,
                format: LevelFormat.DECIMAL,
                text: "%1.",
                alignment: AlignmentType.LEFT,
                style: { paragraph: { indent: { left: 720, hanging: 360 } } },
              }],
            }],
          },
          sections,
        });

        const blob = await Packer.toBlob(doc);
        const suffix = isDefecte ? 'defecte' : 'fara_defecte';
        saveAs(blob, `Act_defectare_${this.formData.id}_${suffix}.docx`);
      } catch (error) {
        console.error(error);
        this.errorMessage = "Eroare la generarea documentului";
      }
    },
    async printProcesVerbal() {
      try {
        const [commission, settings] = await Promise.all([
          api.getPvCommission(),
          api.getSettings(),
        ]);
        const president = commission.length > 0 ? commission[0] : null;
        const members = commission.slice(1);
        const year = String(new Date().getFullYear());
        const ordinNr = settings.ordin_nr || '_____';
        const ordinData = settings.ordin_data || '_____';
        const rectorName = settings.rector_name || '_________________';
        const codFiscal = settings.cod_fiscal || '1007602000972';
        const contabilSef = settings.contabil_sef || '___________________';
        const RO = { language: { value: "ro-RO" } };
        const RO_MD = { language: { value: "ro-MD" } };
        const persoane = [...new Set(this.formData.items.map(i => i.persoana).filter(Boolean))];
        const gestiunePersoana = persoane.join(', ') || '___________________';

        const roRun = (text, opts = {}) => new TextRun({ text, font: "Times New Roman", ...RO, ...opts });
        const boldRoRun = (text, opts = {}) => new TextRun({ text, font: "Times New Roman", bold: true, ...RO, ...opts });
        const tabRun = () => new TextRun({ children: ["\t"], font: "Times New Roman", ...RO });
        const UL = { type: UnderlineType.SINGLE };
        const line = (len) => new TextRun({ text: '_'.repeat(len), font: "Times New Roman", ...RO });

        const thinBorder = { style: BorderStyle.SINGLE, size: 1, color: "000000" };
        const tableBorders = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
        const noBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
        const noBorders = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };

        const COL_W = [480, 2890, 900, 1045, 965, 1070, 1015, 990];
        const TABLE_W = 9355;

        const makeHeaderCell = (text, width) => new TableCell({
          borders: tableBorders,
          verticalAlign: VerticalAlign.CENTER,
          width: { size: width, type: WidthType.DXA },
          children: [new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 0, line: 206 },
            children: [new TextRun({ text, font: "Times New Roman", size: 18, bold: true, ...RO })],
          })],
        });

        const makeNumCell = (text, width) => new TableCell({
          borders: tableBorders,
          verticalAlign: VerticalAlign.CENTER,
          width: { size: width, type: WidthType.DXA },
          shading: { fill: "D9D9D9" },
          children: [new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 0, line: 240 },
            children: [new TextRun({ text, font: "Times New Roman", size: 20, bold: true, ...RO })],
          })],
        });

        const makeDataCell = (text, width, opts = {}) => new TableCell({
          borders: tableBorders,
          verticalAlign: VerticalAlign.CENTER,
          width: { size: width, type: WidthType.DXA },
          children: [new Paragraph({
            alignment: opts.align || AlignmentType.CENTER,
            spacing: { after: 0, line: 240 },
            children: [new TextRun({ text, font: "Times New Roman", size: 20, bold: !!opts.bold, ...RO })],
          })],
        });

        const headerRow = new TableRow({
          height: { value: 835, rule: "atLeast" },
          children: [
            makeHeaderCell("Nr. crt.", COL_W[0]),
            makeHeaderCell("Denumirea obiectului de mijloace fixe", COL_W[1]),
            makeHeaderCell("Nr. de inventar", COL_W[2]),
            makeHeaderCell("Data punerii în funcțiune", COL_W[3]),
            makeHeaderCell("Valoarea de intrare, lei", COL_W[4]),
            makeHeaderCell("Suma uzurii acumulate la data casării, lei", COL_W[5]),
            makeHeaderCell("Durata de funcționare utilă, luni", COL_W[6]),
            makeHeaderCell("Valoarea rămasă probabilă, lei", COL_W[7]),
          ],
        });

        const numberRow = new TableRow({
          height: { value: 240, rule: "exact" },
          children: COL_W.map((w, i) => makeNumCell(String(i + 1), w)),
        });

        let totalValoare = 0;
        let totalUzura = 0;
        const dataRows = this.formData.items.map((item, idx) => {
          // a row holds every unit of the item
          const price = Number(item.pret || 0) * (item.cantitatea || 1);
          totalValoare += price;
          totalUzura += price;
          const invNumbers = this.inventoryNumbersOf(item).join(', ');
          const dataPunerii = this.formatDateShort(item.data_punerii);
          const durata = item.lunii || this.monthDiff(item.data_punerii, this.formData.created_at);
          return new TableRow({
            children: [
              makeDataCell(String(idx + 1), COL_W[0]),
              makeDataCell(item.denumire || '', COL_W[1], { align: AlignmentType.LEFT }),
              makeDataCell(invNumbers, COL_W[2]),
              makeDataCell(dataPunerii, COL_W[3]),
              makeDataCell(price.toFixed(2).replace('.', ','), COL_W[4]),
              makeDataCell(price.toFixed(2).replace('.', ','), COL_W[5]),
              makeDataCell(durata !== '' ? String(durata) : '', COL_W[6]),
              makeDataCell('', COL_W[7]),
            ],
          });
        });

        const totalRow = new TableRow({ children: [
          makeDataCell("", COL_W[0]),
          makeDataCell("Total", COL_W[1], { bold: true }),
          makeDataCell("", COL_W[2]),
          makeDataCell("", COL_W[3]),
          makeDataCell(totalValoare.toFixed(2).replace('.', ','), COL_W[4], { bold: true }),
          makeDataCell(totalUzura.toFixed(2).replace('.', ','), COL_W[5], { bold: true }),
          makeDataCell("", COL_W[6]),
          makeDataCell("", COL_W[7]),
        ]});

        const mainTable = new Table({
          rows: [headerRow, numberRow, ...dataRows, totalRow],
          width: { size: TABLE_W, type: WidthType.DXA },
        });

        const metalsCols = [700, 3555, 2550, 2550];
        const metalsTable = new Table({
          rows: [
            new TableRow({ children: metalsCols.map((w, i) => makeHeaderCell(["Nr.crt.", "Denumirea metalului prețios", "Unitatea de măsură", "Cantitatea"][i], w)) }),
            new TableRow({ children: metalsCols.map((w, i) => makeNumCell(String(i + 1), w)) }),
            new TableRow({ children: metalsCols.map(w => makeDataCell("", w)) }),
          ],
          width: { size: TABLE_W, type: WidthType.DXA },
        });

        const costsCols = [700, 2855, 1600, 2100, 2100];
        const costsTable = new Table({
          rows: [
            new TableRow({ children: costsCols.map((w, i) => makeHeaderCell(["Nr.crt.", "Denumirea articolului de cheltuieli", "Suma, lei", "Denumirea documentului", "Nr. și data documentului"][i], w)) }),
            new TableRow({ children: costsCols.map(w => makeDataCell("", w)) }),
            new TableRow({ children: [makeDataCell("", costsCols[0]), makeDataCell("Total", costsCols[1], { bold: true }), ...costsCols.slice(2).map(w => makeDataCell("", w))] }),
          ],
          width: { size: TABLE_W, type: WidthType.DXA },
        });

        const recCols = [700, 2155, 1400, 1400, 1400, 2300];
        const recoveryTable = new Table({
          rows: [
            new TableRow({ children: recCols.map((w, i) => makeHeaderCell(["Nr.crt.", "Denumirea valorilor materiale", "Unitatea de măsură", "Cantitatea", "Prețul, lei", "Suma, lei"][i], w)) }),
            new TableRow({ children: recCols.map(w => makeDataCell("", w)) }),
            new TableRow({ children: [makeDataCell("", recCols[0]), makeDataCell("Total", recCols[1], { bold: true }), ...recCols.slice(2).map(w => makeDataCell("", w))] }),
          ],
          width: { size: TABLE_W, type: WidthType.DXA },
        });

        const defectText = this.formData.items.map(i => i.defect_description).filter(Boolean).join('; ') || 'uzat complet, nereparabil';
        const codCatalog = [...new Set(this.formData.items.map(i => i.categoryEntity && i.categoryEntity.cod_catalog).filter(Boolean))].join(', ') || '______';

        // Helper: borderless cell for commission/signature tables
        const noCell = (children, width) => new TableCell({
          borders: noBorders,
          width: width ? { size: width, type: WidthType.DXA } : undefined,
          children: Array.isArray(children) ? children : [children],
        });
        const pRun = (text, opts = {}) => new Paragraph({ spacing: { after: 0, line: 240 }, alignment: opts.align || AlignmentType.CENTER, children: [
          new TextRun({ text, font: "Times New Roman", size: opts.size || 24, bold: !!opts.bold, underline: opts.ul ? UL : undefined, ...RO, ...(opts.lang || {}) }),
        ]});
        const labelP = (text) => new Paragraph({ spacing: { after: 0, line: 240 }, alignment: AlignmentType.CENTER, children: [
          new TextRun({ text, font: "Times New Roman", size: 20, ...RO }),
        ]});
        const lineP = () => pRun('_'.repeat(25), { ul: false });

        // Commission members table (borderless, 2 cols: position | name)
        const commissionRows = members.flatMap(exp => [
          new TableRow({ children: [
            noCell(pRun(exp.position || '___', { ul: true }), 4678),
            noCell(pRun(exp.name, { ul: true, lang: RO_MD }), 4677),
          ]}),
          new TableRow({ children: [
            noCell(labelP('(funcția)'), 4678),
            noCell(labelP('(numele, prenumele)'), 4677),
          ]}),
        ]);
        const commissionTable = commissionRows.length > 0 ? new Table({
          rows: commissionRows,
          width: { size: 9355, type: WidthType.DXA },
        }) : null;

        // Signature table (borderless, 2 cols: name underlined | signature line)
        const sigRows = [
          // President
          new TableRow({ children: [
            noCell(pRun(president ? president.name : '___', { ul: true, lang: RO_MD }), 4678),
            noCell(pRun('_'.repeat(25), { ul: false }), 4677),
          ]}),
          new TableRow({ children: [
            noCell(labelP('(numele, prenumele)'), 4678),
            noCell(labelP('(semnătura)'), 4677),
          ]}),
          // Members
          ...members.flatMap(exp => [
            new TableRow({ children: [
              noCell(pRun(exp.name, { ul: true, lang: RO_MD }), 4678),
              noCell(pRun('_'.repeat(25), { ul: false }), 4677),
            ]}),
            new TableRow({ children: [
              noCell(labelP('(numele, prenumele)'), 4678),
              noCell(labelP('(semnătura)'), 4677),
            ]}),
          ]),
        ];
        const signatureTable = new Table({
          rows: sigRows,
          width: { size: 9355, type: WidthType.DXA },
        });

        const doc = new Document({
          styles: { default: { document: {
            run: { font: "Times New Roman", size: 24 },
            paragraph: { spacing: { after: 0, line: 240, lineRule: "auto" } },
          }}},
          sections: [{
            properties: {
              page: {
                size: { width: 11906, height: 16838 },
                margin: { top: 851, right: 850, bottom: 1134, left: 1701 },
              },
            },
            children: [
              // === Annexa header (right, 10pt bold, single line spacing) ===
              new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { after: 0, before: 0, line: 240 }, children: [
                new TextRun({ text: 'Anexa nr.2', font: "Times New Roman", size: 20, bold: true, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { after: 0, before: 0, line: 240 }, children: [
                new TextRun({ text: 'la Regulamentul privind casare', font: "Times New Roman", size: 20, bold: true, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { after: 0, before: 0, line: 240 }, children: [
                new TextRun({ text: 'bunurilor uzate, raportate la mijloacelor fixe', font: "Times New Roman", size: 20, bold: true, ...RO }),
              ]}),

              // === USARB + labels (LEFT aligned) ===
              new Paragraph({ spacing: { after: 0, line: 280 }, children: [
                new TextRun({ text: '                         USARB', font: "Times New Roman", size: 28, ...RO }),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 226 }, children: [
                new TextRun({ text: '              (denumirea întreprinderii)', font: "Times New Roman", size: 20, ...RO }),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 280 }, children: [
                new TextRun({ text: '              ' + codFiscal, font: "Times New Roman", size: 28, ...RO }),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 226 }, children: [
                new TextRun({ text: '                       (codul fiscal)', font: "Times New Roman", size: 20, ...RO }),
              ]}),

              // === APROB block (right-aligned) ===
              new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { after: 0, line: 316 }, children: [
                new TextRun({ text: 'APROB', font: "Times New Roman", size: 28, bold: true, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { after: 0, line: 316 }, children: [
                new TextRun({ text: 'Rectorul Universității de stat "Alecu Russo" din Bălți', font: "Times New Roman", underline: UL, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { after: 0, line: 226 }, children: [
                new TextRun({ text: '(funcția)', font: "Times New Roman", size: 20, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { after: 0, line: 280 }, children: [
                new TextRun({ text: '____________ ', font: "Times New Roman", size: 28, ...RO }),
                new TextRun({ text: rectorName, font: "Times New Roman", size: 28, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { after: 0, line: 240 }, children: [
                new TextRun({ text: '     (semnătura)                    (numele, prenumele)', font: "Times New Roman", size: 20, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { after: 0, line: 268 }, children: [
                roRun('„'),
                line(5),
                roRun('" '),
                line(15),
                roRun('20'),
                line(5),
              ]}),

              // === Spacer ===
              new Paragraph({ spacing: { after: 0, line: 200 }, children: [] }),

              // === PROCES-VERBAL title (centered, 13pt bold, NO indents) ===
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 0, line: 240 }, children: [
                new TextRun({ text: 'PROCES-VERBAL', font: "Times New Roman", size: 26, bold: true, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 0, line: 322 }, children: [
                new TextRun({ text: 'de casare a mijloacelor fixe', font: "Times New Roman", size: 26, bold: true, ...RO }),
              ]}),
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 0, line: 315 }, children: [
                new TextRun({ text: 'nr. ________ din „__________________" ' + year, font: "Times New Roman", size: 26, bold: true, ...RO }),
              ]}),

              // === Spacer ===
              new Paragraph({ spacing: { after: 0, line: 280 }, children: [] }),

              // === Commission intro ===
              new Paragraph({ spacing: { after: 0, line: 271 }, children: [
                roRun('În baza ordinului nr.  '),
                new TextRun({ text: ordinNr, font: "Times New Roman", underline: { type: UnderlineType.SINGLE }, ...RO }),
                tabRun(),
                roRun('din „ '),
                new TextRun({ text: ordinData, font: "Times New Roman", underline: { type: UnderlineType.SINGLE }, ...RO }),
                tabRun(),
                roRun(', comisia în componența președintelui Comisiei'),
              ]}),

              // === President (borderless table row) ===
              ...(president ? [
                new Table({
                  rows: [
                    new TableRow({ children: [
                      noCell(pRun(president.position || '___', { ul: true }), 4678),
                      noCell(pRun(president.name, { ul: true, lang: RO_MD }), 4677),
                    ]}),
                    new TableRow({ children: [
                      noCell(labelP('(funcția)'), 4678),
                      noCell(labelP('(numele, prenumele)'), 4677),
                    ]}),
                  ],
                  width: { size: 9355, type: WidthType.DXA },
                }),
              ] : []),

              // === Members ===
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [
                roRun('și membrilor comisiei:'),
              ]}),
              ...(commissionTable ? [commissionTable] : []),

              // === "a intocmit..." ===
              new Paragraph({ spacing: { after: 0, line: 271 }, children: [
                roRun('a întocmit prezentul proces-verbal de casare a următorului obiect de mijloace fixe:'),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 280 }, children: [] }),

              // === Main table ===
              mainTable,
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),

              // === Asset classification ===
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                roRun('Codul  de  clasificare  a  obiectului  de  mijloace  fixe  conform  Catalogului  mijloacelor  fixe  și  activelor nemateriale '),
                boldRoRun(codCatalog),
                roRun(', categoria de proprietate ______ .'),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                roRun('Data de fabricare (de construire)  _________________________, nr. pașaportului tehnic      Numărul reparațiilor capitale_______________________în valoare de  ______________________________.'),
              ]}),
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                roRun('Data ultimei reparației capitale _________________________.'),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),

              // === Precious metals ===
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [
                roRun('Conținutul de metale prețioase în obiectul de mijloace fixe (grupa de obiecte)'),
              ]}),
              metalsTable,
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),

              // === Technical condition ===
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                roRun('Starea tehnică și motivele casării obiectului de mijloace fixe:  '),
                boldRoRun(defectText),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),

              // === Gestiune ===
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, indent: { firstLine: 708 }, children: [
                roRun('Obiectul de mijloace fixe este casat din gestiunea  _______________________________.'),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 240 }, indent: { left: 1440 }, children: [
                new TextRun({ text: '(denumirea secției, sectorului, serviciului, locului de exploatare)', font: "Times New Roman", size: 20, ...RO }),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),

              // === Conclusion ===
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, children: [
                roRun('Concluzia comisiei: '),
                boldRoRun('nereparabil, de decontat'),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),

              // === Write-off amount ===
              new Paragraph({ alignment: AlignmentType.JUSTIFIED, spacing: { after: 0, line: 240 }, indent: { firstLine: 708 }, children: [
                roRun('Obiectul de mijloace fixe sus-menționat în sumă de       '),
                boldRoRun(totalValoare.toFixed(2).replace('.', ',')),
                roRun('   se casează.'),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [
                roRun('Documentele anexate: act de defectare'),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),

              // === Costs table ===
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [
                boldRoRun('Cheltuieli legate de casarea obiectului de mijloace fixe'),
              ]}),
              costsTable,
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),

              // === Recovery table ===
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [
                boldRoRun('Recuperări în urma casării obiectului de mijloace fixe'),
              ]}),
              recoveryTable,
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),

              // === Signatures ===
              new Paragraph({ spacing: { after: 0, line: 360 }, children: [
                boldRoRun('Președintele comisiei'),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [
                boldRoRun('Membrii comisiei:'),
              ]}),
              signatureTable,
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),

              // === Gestiunea casare person ===
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [
                boldRoRun('Obiectul de mijloace fixe sus-menționat a fost casat din gestiunea'),
              ]}),
              new Table({
                rows: [
                  new TableRow({ children: [
                    noCell(pRun('_'.repeat(20), { ul: false }), 3118),
                    noCell(pRun(gestiunePersoana, { ul: true }), 3118),
                    noCell(pRun('_'.repeat(20), { ul: false }), 3119),
                  ]}),
                  new TableRow({ children: [
                    noCell(labelP('(funcția)'), 3118),
                    noCell(labelP('(numele, prenumele)'), 3118),
                    noCell(labelP('(semnătura)'), 3119),
                  ]}),
                ],
                width: { size: 9355, type: WidthType.DXA },
              }),
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),

              // === Accounting note ===
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [
                boldRoRun('Mențiunea contabilității privind înregistrarea obiectului de mijloace fixe casat:'),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [
                line(30),
                roRun('nr. '),
                line(5),
                roRun(' din „'),
                line(5),
                roRun('" '),
                line(20),
                roRun('20'),
                line(5),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 240 }, indent: { left: 2880 }, children: [
                new TextRun({ text: '(denumirea documentului, registrului)', font: "Times New Roman", size: 20, ...RO }),
              ]}),
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [] }),

              // === Contabil-șef ===
              new Table({
                rows: [
                  new TableRow({ children: [
                    noCell(new Paragraph({ spacing: { after: 0, line: 240 }, children: [boldRoRun('Contabil-șef')] }), 2400),
                    noCell(pRun('_'.repeat(20), { ul: false }), 3478),
                    noCell(pRun(contabilSef, { ul: true }), 3477),
                  ]}),
                  new TableRow({ children: [
                    noCell(labelP(''), 2400),
                    noCell(labelP('(semnătura)'), 3478),
                    noCell(labelP('(numele, prenumele)'), 3477),
                  ]}),
                ],
                width: { size: 9355, type: WidthType.DXA },
              }),
              new Paragraph({ spacing: { after: 0, line: 240 }, children: [
                roRun('„'),
                line(5),
                roRun('" '),
                line(20),
                roRun('20'),
                line(5),
              ]}),
            ],
          }],
        });

        const blob = await Packer.toBlob(doc);
        saveAs(blob, `Proces_verbal_casare_${this.formData.id}.docx`);
      } catch (error) {
        console.error(error);
        this.errorMessage = 'Eroare la generarea documentului Proces-Verbal';
      }
    },
    async handleEditSuccess() {
      this.showEditForm = false;
      this.successMessage = "Formularul a fost actualizat cu succes";
      await this.fetchForm(this.formData.id);
      this.$emit("updated");
    },
    async handleCopySuccess(created) {
      this.showCopyForm = false;
      this.$emit("updated");
      // continue on the new form
      if (created && created.id) await this.fetchForm(created.id);
      this.successMessage = `Formularul nr. ${this.formData.id} a fost creat`;
    },
    handleEditError(msg) {
      this.errorMessage = msg;
    },
    async deleteForm() {
      try {
        await api.deleteForm(this.formData.id);
        this.dialogDelete = false;
        this.$emit("updated");
        this.handleClose();
      } catch (error) {
        this.dialogDelete = false;
        this.errorMessage = error.response?.data?.message || "Eroare la ștergerea formularului";
      }
    },
    async confirmFormAction() {
      this.confirming = true;
      try {
        await api.confirmForm(this.formData.id);
        this.successMessage = "Formularul a fost confirmat cu succes";
        await this.fetchForm(this.formData.id);
        this.$emit("updated");
      } catch (error) {
        this.errorMessage = error.response?.data?.message || "Eroare la confirmarea formularului";
      } finally {
        this.confirming = false;
      }
    },
    async updateStatus(invId, newStatus) {
      try {
        await api.updateInventoryStatus(invId, newStatus);
        this.successMessage = "Starea a fost actualizată";
        await this.fetchForm(this.formData.id);
        this.$emit("updated");
      } catch (error) {
        this.errorMessage = "Eroare la actualizarea statusului";
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatDateShort(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}.${mm}.${yyyy}`;
    },
    monthDiff(startStr, endStr) {
      if (!startStr) return '';
      const start = new Date(startStr);
      const end = new Date(endStr);
      return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    },
    handleClose() {
      this.dialog = false;
      this.$emit("close");
    },
    onEscKey(event) {
      if (event.key === "Escape") {
        this.handleClose();
      }
    },
    clearMessage() {
      this.errorMessage = "";
      this.successMessage = "";
    },
  },
  computed: {
    ...mapGetters(["getUser"]),
    createdByName() {
      if (!this.formData || !this.formData.createdByUser) return '';
      return `${this.formData.createdByUser.first_name} ${this.formData.createdByUser.last_name}`;
    },
    canUpdateStatus() {
      const role = this.getUser.role;
      return role === 'inginer' || role === 'inginer-pc' || role === 'admin';
    },
    canConfirm() {
      if (!this.formData) return false;
      // All inventory numbers must be finished (or no inventory numbers at all)
      for (const item of this.formData.items) {
        if (item.inventoryNumbers && item.inventoryNumbers.length > 0) {
          for (const inv of item.inventoryNumbers) {
            if (inv.status !== 'finished') return false;
          }
        }
      }
      return true;
    },
  },
  mounted() {
    this.fetchForm(this.formId);
    document.addEventListener("keydown", this.onEscKey);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onEscKey);
  },
};
</script>

<style scoped>
.space {
  padding-top: 20px;
  background-color: white;
}
.custom-heading {
  color: #424242;
  text-align: center;
}
.light-green-row {
  background-color: #ecf9f2;
}
.defect-row {
  font-style: italic;
  background: #fff3f0;
  border-radius: 4px;
  padding: 6px 10px !important;
}
</style>

